'use client'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { StylePanelProps } from '@/lib/stylePanel'
import useResponsivePadding from '@/lib/useResponsivePadding'

import { PhnxTag } from '../Tag/Tag'
import styles from './Griditem.module.css'

export type GriditemProps = {
  asset?: {
    filename: string
    alt: string
    layout?: 'default' | 'small' | 'large' | 'full' | 'xsmall' | 'inline' | 'ssmall'
    align?: 'left' | 'right' | 'center'
    width?: number
    id?: string
  } | null
  title?: string
  body?: React.ReactNode
  button?: React.ReactNode
  tag?: string
  layout?: any
  assetBoxShadow?: boolean
  itemBoxShadow?: boolean
  link?: any
  feature?: boolean
  roundedCorners?: boolean
} & StylePanelProps

export const PhnxGriditem: React.FC<GriditemProps> = ({
  title,
  asset = { filename: '', alt: '', layout: 'default', align: 'left', width: 0 },
  body,
  button,
  assetBoxShadow = false,
  itemBoxShadow = false,
  roundedCorners = false,
  padding,
  backgroundColor,
  textColor,
  alignment = 'left',
  layout = 'default',
  tag,
  link,
  feature,
}) => {
  const paddingStyles = useResponsivePadding(padding)

  const style = { ...paddingStyles, backgroundColor, color: textColor, height: '100%' }

  const [isClient, setIsClient] = useState(false)
  const isDesktop = useMediaQuery({ query: '(min-width: 600px)' }, undefined, (matches) => {
    if (isClient) return matches
    return false // Default to mobile on server
  })

  useEffect(() => {
    setIsClient(true)
  }, [])

  // if asset is /public/assets/ then don't use /m/
  const imageUrl = asset?.filename
    ? asset.filename.startsWith('/assets/')
      ? asset.filename
      : `${asset.filename}/m/`
    : ''

  let assetHeight: number | null = 150
  let assetWidth: string | number = 'auto'

  // Set asset dimensions based on layout - this should work consistently on server and client
  if (asset?.layout === 'ssmall') {
    assetHeight = 40
  } else if (asset?.layout === 'xsmall') {
    assetHeight = 30
  } else if (asset?.layout === 'small') {
    assetHeight = 50
  } else if (asset?.layout === 'large') {
    assetHeight = 70
  } else if (asset?.layout === 'full') {
    assetHeight = null
  }

  // Only override with custom width on desktop and when client-side
  if (asset?.width !== 0 && asset?.width !== undefined && isClient && isDesktop) {
    assetHeight = null
    assetWidth = asset?.width
  }

  const handleClick = () => {
    if (link) {
      window.open(link.cached_url, '_blank')
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleClick()
    }
  }

  return (
    <div style={style} className={`stylepanel ${styles[roundedCorners ? 'roundedCorners' : '']}`}>
      <style jsx>{`
        stylepanel {
          ${paddingStyles}
        }
      `}</style>

      {link && link.cached_url !== '' ? (
        <div
          className={`${styles['griditem']} ${styles[`alignment-${alignment}`]} ${styles[`linkcursor`]} ${styles[`layout-${layout}`]} ${itemBoxShadow && styles['item-box-shadow']}`}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="button"
          aria-pressed="false"
        >
          {asset && asset.filename && (
            <div
              style={{
                width: isClient && assetWidth !== 'auto' ? `${assetWidth}px` : 'auto',
                maxWidth: '110%',
              }}
              className={`${styles['asset-container']} ${styles[`alignment-${asset.align}`]} ${assetBoxShadow && styles['box-shadow']} ${layout === 'secondary' && styles['secondary']}`}
            >
              <Image
                src={imageUrl}
                width={0}
                height={0}
                sizes="100vw"
                alt={asset.alt}
                style={{
                  width: isClient && assetWidth !== 'auto' ? `${assetWidth}px` : 'auto',
                  height: assetHeight ? `${assetHeight}px` : 'auto',
                }}
              />
              {layout === 'secondary' && title && <h2>{title}</h2>}
            </div>
          )}
          <div className="text-container">
            {tag && (
              <div className="tag">
                <PhnxTag title={tag} isLight isBold />
              </div>
            )}
            {layout !== 'secondary' && title && <h2>{title}</h2>}
            {body && <div className="body">{body}</div>}
            {button && <div className="button-container">{button}</div>}
          </div>
        </div>
      ) : (
        <div
          className={`${styles['griditem']} ${styles[`alignment-${alignment}`]} ${styles[`layout-${layout}`]} ${itemBoxShadow && styles['item-box-shadow']}`}
        >
          {asset && asset.filename && (
            <div
              style={{
                width: isClient && assetWidth !== 'auto' ? `${assetWidth}px` : 'auto',
                maxWidth: '110%',
              }}
              className={`${styles['asset-container']} ${styles[`alignment-${asset.align}`]} ${assetBoxShadow && styles['box-shadow']} ${layout === 'secondary' && styles['secondary']}`}
            >
              <Image
                src={imageUrl}
                width={0}
                height={0}
                sizes="100vw"
                alt={asset.alt}
                style={{
                  width: isClient && assetWidth !== 'auto' ? `${assetWidth}px` : 'auto',
                  height: assetHeight ? `${assetHeight}px` : 'auto',
                }}
              />
              {layout === 'secondary' && title && <h2>{title}</h2>}
            </div>
          )}
          <div className="text-container">
            {tag && (
              <div className="tag">
                <PhnxTag title={tag} isLight isBold />
              </div>
            )}
            {layout !== 'secondary' && title && (
              <h2 className={`${feature ? styles['feature-title'] : ''}`}>{title}</h2>
            )}
            {body && <div className="body">{body}</div>}
            {button && <div className="button-container">{button}</div>}
          </div>
        </div>
      )}
    </div>
  )
}
